import { useEffect, useRef, useState } from "react"
import { Shell } from "@/components/Shell"
import { MethodSteps } from "@/components/MethodSteps"
import {
  listRecipes,
  parseRecipe,
  saveRecipe,
  type RecipeSummary,
  type ResolvedRecipe,
  type ResolvedIngredient,
} from "@/lib/api"
import {
  useBackgroundJobs,
  useDebouncedEffect,
  useDraft,
} from "@/lib/backgroundWork"

// Page that originates parse jobs on this surface — stamped on the
// tracked BgJob so the pill returns the user here, not to /today.
const RECIPES_NEW_ORIGIN = "/recipes/new"

/**
 * RecipesNew — paste-or-name → Claude parse → USDA resolve → review → save.
 *
 * Parse runs through the BackgroundWorkProvider so a tab-switch mid-parse
 * doesn't lose the job. The local "phase" comes purely from provider
 * state (idle → polling → review → error) — there's no internal
 * setTimeout poll loop here anymore.
 *
 * The "saved" phase IS local: it's the post-commit confirmation card,
 * not a background job. Once the user clicks "add another" we clear it.
 */
export function RecipesNewPage() {
  // Inputs persist across navigation. Same TTL contract as the meal
  // composer (1h) — long enough for an interrupted flow, short enough
  // not to surprise someone the next day.
  const [tab, setTab] = useDraft<"text" | "name" | "url" | "image">(
    "recipes-new:tab",
    "text",
  )
  const [content, setContent, clearContent] = useDraft<string>(
    "recipes-new:content",
    "",
  )
  // Image source is purely in-memory — base64 blobs are too large to
  // persist sanely (multi-image upload could be 5MB+, blowing the
  // localStorage quota). Navigation = re-attach. Documented in #27.
  const [images, setImages] = useState<UserImage[]>([])

  const { jobForOrigin, trackParseJob, dismissJob } = useBackgroundJobs()
  const job = jobForOrigin(RECIPES_NEW_ORIGIN)

  // Post-save confirmation lives outside the provider — it's ephemeral
  // by design (an "ok, saved" pat-on-the-back UI), not background work.
  const [saved, setSaved] = useState<{ id: string; name: string } | null>(
    null,
  )
  // Enqueue-time error (server returned non-2xx on POST /recipes/parse).
  // Distinct from a parse-itself failure which lives on the BgJob.
  const [enqueueErr, setEnqueueErr] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setEnqueueErr(null)
    setSaved(null)
    // Source-type-specific validation gates the request.
    if (tab === "image") {
      if (images.length === 0) return
    } else if (!content.trim()) {
      return
    }
    try {
      let job_id: string
      let label: string
      if (tab === "image") {
        const payload = images.map((img) => ({
          base64: img.base64,
          mime: img.mime,
        }))
        const out = await parseRecipe("photo", "", payload)
        job_id = out.job_id
        label =
          images.length === 1
            ? `Photo · ${images[0].name}`
            : `${images.length} photos`
      } else {
        const out = await parseRecipe(tab, content.trim())
        job_id = out.job_id
        label =
          content.length > 60 ? content.slice(0, 57).trimEnd() + "…" : content
      }
      trackParseJob(job_id, label, RECIPES_NEW_ORIGIN)
    } catch (err: any) {
      setEnqueueErr(err?.response?.data?.error || err?.message)
    }
  }

  // Reset everything — used after a successful save and after explicit
  // discards from the review/error states.
  function resetAll() {
    if (job) dismissJob(job.id)
    clearContent()
    setImages([])
    setEnqueueErr(null)
  }

  return (
    <Shell
      currentPath="/recipes/new"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes/new", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3">recipe · parse + review</p>
        <h1 className="font-display text-display tracking-tightest mb-10">
          Paste a recipe.{" "}
          <span className="italic text-moss">We'll do the math.</span>
        </h1>

        {/* Phase derivation from provider state. saved card (post-commit)
            wins because it's a confirmation; otherwise the BgJob's
            status drives the rest. */}
        {saved ? null : !job && (
          <form onSubmit={onSubmit} className="space-y-8">
            <SourcePicker
              active={tab}
              onChange={(id) => {
                // pdf is still disabled at the picker; image is live now.
                // The runtime gate in SOURCES (available:false) prevents
                // dispatch for pdf, so this narrowing is safe.
                if (id === "pdf") return
                setTab(id)
                setContent("")
                setImages([])
              }}
              renderInput={(activeId) => {
                if (activeId === "text") {
                  return (
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={10}
                      placeholder={`Paste anywhere — formal recipes, casual prose, bullet lists.\n\nExample:\n  1 cup chickpeas, soaked overnight then boiled\n  2 tbsp tahini\n  juice of 1 lemon\n  3 cloves garlic\n  Blend until smooth.`}
                      className="w-full bg-transparent border border-border focus:border-moss focus:outline-none rounded p-4 font-sans text-base leading-relaxed"
                    />
                  )
                }
                if (activeId === "name") {
                  return (
                    <DishNameInput
                      value={content}
                      onChange={setContent}
                    />
                  )
                }
                if (activeId === "image") {
                  return (
                    <ImageInput
                      value={images}
                      onChange={setImages}
                    />
                  )
                }
                if (activeId === "url") {
                  return (
                    <>
                      <input
                        type="url"
                        inputMode="url"
                        autoComplete="off"
                        spellCheck={false}
                        value={content}
                        onChange={(e) => setContent(e.target.value.trim())}
                        placeholder="https://cooking.nytimes.com/…  ·  https://serious-eats.com/…"
                        className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none py-3 font-sans text-base"
                      />
                      <p className="font-sans text-xs text-muted-foreground mt-3 leading-relaxed">
                        We fetch the page server-side, strip ads/nav, and hand
                        the recipe content to Claude. Works on most recipe
                        sites; some paywalled or JS-rendered pages may parse
                        poorly.
                      </p>
                      {content && !isLikelyURL(content) && (
                        <p className="font-sans text-xs text-persimmon mt-2">
                          Doesn't look like a URL — should start with http://
                          or https://
                        </p>
                      )}
                    </>
                  )
                }
                return null
              }}
            />

            <button
              type="submit"
              disabled={
                tab === "image" ? images.length === 0 : !content.trim()
              }
              className="bg-foreground text-background px-6 py-3.5 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {tab === "image"
                ? images.length > 1
                  ? `Parse ${images.length} photos →`
                  : "Parse photo →"
                : "Parse with Claude →"}
            </button>
            {enqueueErr && (
              <p className="font-sans text-sm text-persimmon">{enqueueErr}</p>
            )}
          </form>
        )}

        {/* Polling: parse is enqueued, server is working. The provider
            keeps polling regardless of navigation; this card is purely
            visual. */}
        {!saved && job && (job.status === "pending" || job.status === "running") && (
          <PollingCard createdAt={job.createdAt} onCancel={resetAll} />
        )}

        {!saved && job && job.status === "succeeded" && job.result && (
          <RecipesReviewHost
            jobId={job.id}
            result={job.result}
            edits={job.reviewEdits}
            onSaved={(out) => {
              setSaved({ id: out.id, name: out.name })
              if (job) dismissJob(job.id)
              clearContent()
            }}
            onCancel={resetAll}
          />
        )}

        {saved && (
          <div className="card-surface px-8 py-10">
            <p className="eyebrow text-moss mb-3">saved</p>
            <h2 className="font-display text-3xl tracking-tight mb-3">{saved.name}</h2>
            <p className="font-sans text-sm text-muted-foreground mb-6">
              You can now log this recipe in the Today view — pick "{saved.name}" from the ingredient search.
            </p>
            <div className="flex gap-3">
              <a href="/today" className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors">
                Log it now →
              </a>
              <a href="/recipes" className="border border-foreground/30 text-foreground px-5 py-2.5 rounded font-sans text-sm hover:border-moss hover:text-moss transition-colors">
                View library
              </a>
              <button
                type="button"
                onClick={() => setSaved(null)}
                className="eyebrow"
              >
                add another
              </button>
            </div>
          </div>
        )}

        {!saved && job && (job.status === "failed" || job.status === "expired") && (
          <div className="space-y-4">
            <p className="font-sans text-sm text-persimmon">
              {job.error ||
                (job.status === "expired"
                  ? "The parser took too long and the job was reaped. Try again?"
                  : "Parse failed.")}
            </p>
            <button type="button" onClick={resetAll} className="eyebrow">
              ← try again
            </button>
          </div>
        )}
      </div>
    </Shell>
  )
}

// ---- Polling card (shown while server is parsing) ----

function PollingCard({
  createdAt,
  onCancel,
}: {
  createdAt: number
  onCancel: () => void
}) {
  // Elapsed comes from createdAt (provider-owned), so a user who kicks
  // off a parse, switches tabs for 12s, and comes back sees "12s"
  // immediately rather than "0s".
  const [elapsed, setElapsed] = useState(
    Math.floor((Date.now() - createdAt) / 1000),
  )
  useEffect(() => {
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - createdAt) / 1000)),
      500,
    )
    return () => clearInterval(id)
  }, [createdAt])
  return (
    <div className="card-surface px-8 py-12 grid place-items-center text-center">
      <div className="space-y-4 max-w-md">
        <p className="eyebrow">parsing · {elapsed}s elapsed</p>
        <p className="font-display text-2xl italic text-moss">
          Reading the recipe…
        </p>
        <p className="font-sans text-sm text-muted-foreground">
          Claude is extracting ingredients, then looking each one up in USDA.
          Typically 5–15 seconds. You can leave this page — we'll keep parsing
          in the background.
        </p>
        <div className="h-px w-full bg-border relative overflow-hidden mt-6">
          <div className="absolute inset-y-0 left-0 w-1/3 bg-moss animate-[shimmer_2s_ease-in-out_infinite]" />
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="eyebrow text-muted-foreground hover:text-persimmon transition-colors"
        >
          cancel
        </button>
      </div>
    </div>
  )
}

// ---- Review host + view ----

/**
 * RecipesReviewHost — owns the local ResolvedRecipe edit state, mirrors
 * changes back to the provider's reviewEdits so a tab-switch mid-review
 * doesn't lose the user's per-ingredient tweaks. On save, we hand the
 * recipe off to the API and the parent dismisses the job.
 */
function RecipesReviewHost({
  jobId,
  result,
  edits,
  onSaved,
  onCancel,
}: {
  jobId: string
  result: ResolvedRecipe
  edits?: { ingredients?: ResolvedIngredient[]; nameOverride?: string }
  onSaved: (out: { id: string; name: string }) => void
  onCancel: () => void
}) {
  const { updateJobEdits } = useBackgroundJobs()

  // Seed from provider edits if present, else the raw parse result.
  // The `recompute` call ensures totals/per_100g reflect whatever
  // ingredients were persisted (in case the user edited quantities,
  // closed the tab, came back).
  const initial: ResolvedRecipe = edits?.ingredients
    ? recompute({ ...result, ingredients: edits.ingredients })
    : result
  const initialName = edits?.nameOverride ?? initial.name

  return (
    <ReviewView
      jobId={jobId}
      initial={{ ...initial, name: initialName }}
      onChange={(r) =>
        updateJobEdits(jobId, {
          ingredients: r.ingredients,
          nameOverride: r.name,
        })
      }
      onSave={async (final) => {
        const out = await saveRecipe(final)
        onSaved({ id: out.id, name: out.name })
      }}
      onCancel={onCancel}
    />
  )
}

function ReviewView({
  initial,
  onChange,
  onSave,
  onCancel,
}: {
  // jobId is passed so a fresh mount (e.g. tab switch) creates a new
  // local state via React's reconciliation by key.
  jobId: string
  initial: ResolvedRecipe
  onChange: (r: ResolvedRecipe) => void
  onSave: (final: ResolvedRecipe) => Promise<void>
  onCancel: () => void
}) {
  const [r, setR] = useState<ResolvedRecipe>(initial)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Sync edits back to the parent (which writes to the provider).
  // Debounced 300ms so per-keystroke recipe-name edits don't thrash
  // localStorage. Flush-on-unmount keeps the last burst.
  useDebouncedEffect(
    () => {
      onChange(r)
    },
    [r],
    300,
  )

  function updateIng(idx: number, patch: Partial<ResolvedIngredient>) {
    const ings = r.ingredients.slice()
    ings[idx] = { ...ings[idx], ...patch }
    const updated = recompute({ ...r, ingredients: ings })
    setR(updated)
  }

  function removeIng(idx: number) {
    const ings = r.ingredients.filter((_, i) => i !== idx)
    setR(recompute({ ...r, ingredients: ings }))
  }

  function changeYield(v: number) {
    setR(recompute({ ...r, total_yield_g: v }))
  }

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <div className="flex items-baseline gap-3">
          <label className="block flex-1">
            <span className="eyebrow block mb-2">recipe name</span>
            <input
              type="text"
              value={r.name}
              onChange={(e) => setR({ ...r, name: e.target.value })}
              className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none py-2 font-display text-2xl"
            />
          </label>
          {r.confidence === "low" && (
            <span className="eyebrow text-persimmon">low confidence</span>
          )}
        </div>
        {r.method_summary && (
          <div>
            <p className="eyebrow mb-3">method</p>
            <MethodSteps text={r.method_summary} />
          </div>
        )}
      </header>

      {/* Per-100g + total preview */}
      <section className="grid sm:grid-cols-2 gap-px bg-border card-surface overflow-hidden">
        <YieldBlock label="per 100g" macros={r.per_100g} />
        <YieldBlock label={`total · ${r.total_yield_g.toFixed(0)} g`} macros={r.totals} />
      </section>

      {/* Yield override */}
      <label className="flex items-baseline gap-3 font-sans text-sm">
        <span className="eyebrow">total yield (g)</span>
        <input
          type="number"
          value={Math.round(r.total_yield_g)}
          onChange={(e) => changeYield(parseFloat(e.target.value) || 0)}
          className="bg-transparent border-b border-border focus:border-moss outline-none py-1 px-2 w-28 font-mono text-base text-right num"
        />
        <span className="font-sans text-xs text-muted-foreground">override Claude's estimate if you measured differently</span>
      </label>

      {/* Ingredients table.
          Layout: stacked rows on mobile (full-width name input on top, then a
          flex row with qty/kcal/protein/source/×) so long ingredient names
          like "Garlic cloves, peeled" don't get truncated to "Garlic clov"
          inside a fixed 2fr column. On sm+ it's still a 6-column grid for
          dense editing on desktop. */}
      <section>
        <p className="eyebrow mb-3">ingredients · {r.ingredients.length}</p>
        <div className="border-t border-b border-border">
          <div className="hidden sm:grid grid-cols-[2fr_auto_auto_auto_auto_auto] gap-3 py-2 px-1 eyebrow border-b border-border">
            <span>name · state</span>
            <span className="text-right">qty (g)</span>
            <span className="text-right">kcal/100g</span>
            <span className="text-right">protein</span>
            <span className="text-right">source</span>
            <span></span>
          </div>
          {r.ingredients.map((ing, i) => (
            <div
              key={i}
              className="flex flex-col sm:grid sm:grid-cols-[2fr_auto_auto_auto_auto_auto] sm:gap-3 sm:items-center py-3 px-1 border-b border-border last:border-b-0 gap-2"
            >
              <input
                type="text"
                value={ing.name}
                onChange={(e) => updateIng(i, { name: e.target.value })}
                className="bg-transparent border-0 border-b border-border sm:border-b-0 focus:border-moss outline-none font-sans text-base sm:text-sm py-1 min-w-0 w-full"
              />
              <div className="flex items-center gap-3 sm:contents">
                <label className="flex items-baseline gap-1 sm:hidden">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">qty</span>
                </label>
                <input
                  type="number"
                  value={ing.quantity_g}
                  onChange={(e) => updateIng(i, { quantity_g: parseFloat(e.target.value) || 0 })}
                  step={5}
                  className="w-16 sm:w-20 text-right bg-transparent border-b border-border focus:border-moss outline-none py-1 font-mono text-sm num"
                />
                <label className="flex items-baseline gap-1 sm:hidden">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">kcal</span>
                </label>
                <input
                  type="number"
                  value={Math.round(ing.calories_per_100g)}
                  onChange={(e) => updateIng(i, { calories_per_100g: parseFloat(e.target.value) || 0 })}
                  className="w-14 sm:w-16 text-right bg-transparent border-b border-border focus:border-moss outline-none py-1 font-mono text-xs num"
                />
                <label className="flex items-baseline gap-1 sm:hidden">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">p</span>
                </label>
                <input
                  type="number"
                  value={ing.protein_per_100g.toFixed(1)}
                  onChange={(e) => updateIng(i, { protein_per_100g: parseFloat(e.target.value) || 0 })}
                  step={0.1}
                  className="w-12 sm:w-14 text-right bg-transparent border-b border-border focus:border-moss outline-none py-1 font-mono text-xs num"
                />
                <span className={`text-[10px] sm:text-xs px-2 py-[2px] border rounded uppercase tracking-smallcaps ${
                  ing.source === "usda" ? "border-moss/40 text-moss" :
                  ing.source === "claude" ? "border-clay/40 text-clay" :
                  "border-border text-muted-foreground"
                }`}>
                  {ing.source}
                </span>
                <button
                  type="button"
                  onClick={() => removeIng(i)}
                  className="text-muted-foreground hover:text-persimmon ml-auto sm:ml-0"
                  title="Remove"
                  aria-label={`Remove ${ing.name}`}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {err && <p className="font-sans text-sm text-persimmon">{err}</p>}

      <div className="flex items-center gap-4">
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true); setErr(null)
            try { await onSave(r) }
            catch (e: any) { setErr(e?.response?.data?.error || e?.message || "Save failed") }
            finally { setBusy(false) }
          }}
          className="bg-foreground text-background px-6 py-3.5 rounded font-sans text-sm hover:bg-moss transition-colors disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save recipe"}
        </button>
        <button type="button" onClick={onCancel} className="eyebrow">discard</button>
      </div>
    </div>
  )
}

function YieldBlock({ label, macros }: { label: string; macros: ResolvedRecipe["per_100g"] }) {
  return (
    <div className="bg-surface px-6 sm:px-8 py-6 space-y-4">
      <p className="eyebrow">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-5xl num tracking-tightest">{macros.calories.toFixed(0)}</span>
        <span className="font-mono text-xs text-muted-foreground">kcal</span>
      </div>
      <div className="grid grid-cols-4 gap-3 text-xs font-mono text-muted-foreground tabular-nums">
        <span>P {macros.protein_g.toFixed(1)}g</span>
        <span>F {macros.fiber_g.toFixed(1)}g</span>
        <span>C {macros.carbs_g.toFixed(1)}g</span>
        <span>S {macros.sugar_g.toFixed(1)}g</span>
      </div>
    </div>
  )
}

// Recompute per-100g + totals client-side mirroring backend.
function recompute(r: ResolvedRecipe): ResolvedRecipe {
  let grams = 0, cal = 0, prot = 0, fib = 0, carb = 0, sug = 0
  for (const ing of r.ingredients) {
    grams += ing.quantity_g
    cal += (ing.quantity_g * ing.calories_per_100g) / 100
    prot += (ing.quantity_g * ing.protein_per_100g) / 100
    fib += (ing.quantity_g * ing.fiber_per_100g) / 100
    carb += (ing.quantity_g * ing.carbs_per_100g) / 100
    sug += (ing.quantity_g * ing.sugar_per_100g) / 100
  }
  const denom = r.total_yield_g > 0 ? r.total_yield_g : grams
  const safeDenom = denom > 0 ? denom : 1
  return {
    ...r,
    total_yield_g: r.total_yield_g > 0 ? r.total_yield_g : grams,
    totals: { calories: cal, protein_g: prot, fiber_g: fib, carbs_g: carb, sugar_g: sug },
    per_100g: {
      calories: (cal / safeDenom) * 100,
      protein_g: (prot / safeDenom) * 100,
      fiber_g: (fib / safeDenom) * 100,
      carbs_g: (carb / safeDenom) * 100,
      sugar_g: (sug / safeDenom) * 100,
    },
  }
}

// Lightweight client-side URL check — just enough to gate the submit button
// and show a hint. Real validation is the server's job (SSRF guard +
// scheme/host/content-type checks live in internal/recipe/url.go).
function isLikelyURL(s: string): boolean {
  if (!s) return false
  try {
    const u = new URL(s.trim())
    return u.protocol === "http:" || u.protocol === "https:"
  } catch {
    return false
  }
}

// ============================================================================
//   DishNameInput
// ============================================================================
//
// Search-existing-first behaviour for the "Dish name" source.
//
// As the user types, debounced query hits /api/v1/recipes?q=… across BOTH
// origins (saved + log) so a previously-logged composed meal — which lives
// in the recipe table under origin='log' — is also offered up for re-use.
// If we have matches we surface them as a quick-pick list; tapping one
// navigates to /recipes/{id}. Only when the user dismisses the suggestions
// (or there genuinely aren't any) does the "Parse with Claude →" path
// become the answer.
//
// Why this matters: previously every "peanut chutney" lookup went straight
// to Claude even if the user had already saved one. That's slow (~3s parse)
// and pollutes the recipes table with duplicates.

function DishNameInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [matches, setMatches] = useState<RecipeSummary[] | null>(null)
  const [dismissed, setDismissed] = useState(false)

  // Debounced search. 200ms felt right in QuickMealLog; keeping that.
  useEffect(() => {
    const q = value.trim()
    if (q.length < 2) {
      setMatches(null)
      return
    }
    setDismissed(false)
    const ctrl = new AbortController()
    const handle = setTimeout(async () => {
      try {
        const rs = await listRecipes("all", undefined, q)
        if (!ctrl.signal.aborted) setMatches(rs)
      } catch {
        if (!ctrl.signal.aborted) setMatches([])
      }
    }, 200)
    return () => {
      clearTimeout(handle)
      ctrl.abort()
    }
  }, [value])

  const hasMatches = !dismissed && matches !== null && matches.length > 0

  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. peanut chutney, hummus, paneer butter masala"
        className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none py-3 font-sans text-lg"
        autoComplete="off"
      />

      {hasMatches && (
        <div className="mt-4 border-l-2 border-moss/50 pl-4 animate-fade-up">
          <div className="flex items-baseline justify-between mb-2">
            <p className="eyebrow">
              already in your library · {matches!.length}
            </p>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="eyebrow text-muted-foreground hover:text-foreground transition-colors"
            >
              parse anyway ↓
            </button>
          </div>
          <ul className="divide-y divide-border border-t border-b border-border">
            {matches!.slice(0, 8).map((r) => (
              <li key={r.id}>
                <a
                  href={`/recipes/${r.id}`}
                  className="block py-3 px-1 hover:bg-muted/40 transition-colors group"
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
                      {r.origin === "log" && (
                        <span
                          className="font-mono text-[10px] uppercase tracking-wider text-moss border border-moss/40 rounded px-1.5 py-0.5"
                          title="Auto-saved by the meal composer"
                        >
                          log
                        </span>
                      )}
                      <p className="font-sans text-base text-foreground group-hover:text-moss transition-colors truncate">
                        {r.name}
                      </p>
                    </div>
                    <span
                      aria-hidden
                      className="font-mono text-sm text-muted-foreground group-hover:text-moss transition-colors whitespace-nowrap"
                    >
                      →
                    </span>
                  </div>
                  <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                    {r.calories_per_100g.toFixed(0)} kcal/100g · yields{" "}
                    {r.total_yield_g}g
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="font-sans text-xs text-muted-foreground mt-3 leading-relaxed">
        {hasMatches
          ? "Tap a match to open it. Or refine the name and we'll keep searching."
          : value.trim().length >= 2 && matches !== null && matches.length === 0
            ? "No matches in your library. Tap Parse to have Claude generate a typical recipe."
            : "Claude will generate a typical recipe. Review + tweak before saving."}
      </p>
    </>
  )
}

// ============================================================================
//   SourcePicker
// ============================================================================
//
// Editorial table-of-contents picker for "where does this recipe come from?"
// Vertical chapter list: numbered eyebrow + display headline + one-line
// description. The active row expands inline below to host the input.
// Disabled "soon" rows still render so the menu is honest about what's
// coming without crowding the live options.
//
// Why this shape (vs a row of pill tabs):
//  - 3 pills already wrapped on 360px-wide phones. 5 pills are unreadable.
//  - Big single-column tap targets are the right ergonomics on mobile.
//  - A vertical numbered list reads as "chapters of a recipe" — on-brand
//    with the rest of the magazine spread aesthetic.
//  - Adding a 6th option (e.g. camera-roll OCR) doesn't require touching
//    this layout — just a new entry in SOURCES.

type SourceID = "text" | "name" | "url" | "image" | "pdf"

type SourceMeta = {
  id: SourceID
  title: string
  desc: string
  available: boolean
}

const SOURCES: SourceMeta[] = [
  { id: "text",  title: "Recipe text", desc: "pasted prose or bullet lists",   available: true },
  { id: "name",  title: "Dish name",   desc: "claude generates a typical recipe", available: true },
  { id: "url",   title: "URL",         desc: "we fetch and parse the page",    available: true },
  { id: "image", title: "Image",       desc: "camera or photo upload",         available: true },
  { id: "pdf",   title: "PDF",         desc: "text extraction — coming soon",  available: false },
]

function SourcePicker({
  active,
  onChange,
  renderInput,
}: {
  active: SourceID
  onChange: (id: SourceID) => void
  renderInput: (id: SourceID) => React.ReactNode
}) {
  return (
    <section>
      <p className="eyebrow mb-3">source</p>
      <ul className="border-t border-border">
        {SOURCES.map((s, i) => {
          const isActive = active === s.id
          const num = String(i + 1).padStart(2, "0")
          return (
            <li
              key={s.id}
              className={`border-b border-border ${
                isActive ? "bg-muted/20" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => s.available && onChange(s.id)}
                disabled={!s.available}
                aria-pressed={isActive}
                aria-disabled={!s.available}
                className={`w-full text-left grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,2fr)_auto] items-center gap-3 sm:gap-6 py-4 px-1 transition-colors ${
                  s.available
                    ? isActive
                      ? ""
                      : "hover:bg-muted/40"
                    : "opacity-40 cursor-not-allowed"
                }`}
              >
                <span
                  className={`font-mono text-xs tabular-nums ${
                    isActive ? "text-moss" : "text-muted-foreground"
                  }`}
                >
                  {num}
                </span>
                <span
                  className={`font-display text-xl sm:text-2xl tracking-tight leading-tight ${
                    isActive ? "text-foreground" : ""
                  }`}
                >
                  {s.title}
                </span>
                <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-snug">
                  {s.desc}
                </span>
                <span
                  aria-hidden
                  className={`font-mono text-base transition-transform duration-200 ${
                    isActive
                      ? "text-moss rotate-90"
                      : s.available
                        ? "text-muted-foreground"
                        : "text-muted-foreground/40"
                  }`}
                >
                  ›
                </span>
              </button>
              {/* sm-hidden description (full-width line beneath the title row on
                  mobile, since we can't fit it on the same line) */}
              {!isActive && (
                <p className="sm:hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground/80 px-1 pb-3 -mt-2 ml-8">
                  {s.desc}
                </p>
              )}
              {isActive && (
                <div className="px-1 pb-6 pt-1 sm:pl-12 animate-fade-up">
                  {renderInput(s.id)}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   IMAGE INPUT — multi-photo recipe source
//
//   Behaviour:
//     - File picker (multiple, JPEG/PNG only). Camera capture hint on mobile.
//     - Per-image client-side compression to keep payload sane (Canvas
//       resize → 1600px long edge, JPEG q=0.82). A 4MB phone photo
//       lands around 800KB, well under the server's 4MB per-image cap.
//     - HEIC rejected at the picker with an inline message — iOS users
//       typically get JPEG from the file picker but raw camera-roll
//       can sometimes leak HEIC.
//     - Thumbnail strip with per-image × and HTML5 drag-and-drop reorder
//       on desktop; ↑/↓ position buttons on each thumb work everywhere
//       (mobile gets reorder via buttons, no Pointer Events impl).
//     - Cap at MAX_IMAGES; + tile disabled when at cap.
// ════════════════════════════════════════════════════════════════════════════

const MAX_IMAGES = 4
const MAX_INPUT_BYTES = 10 * 1024 * 1024  // 10MB raw — beyond this we refuse pre-compression
const RESIZE_MAX_EDGE = 1600
const JPEG_QUALITY = 0.82

type UserImage = {
  // Stable id for React keys + reorder bookkeeping. Random per-file
  // since the same filename can be selected twice (e.g., two pages
  // scanned with identical names from a flat scanner).
  id: string
  name: string
  base64: string
  mime: "image/jpeg" | "image/png"
  /** Compressed size (bytes) — surfaced as a small caption under the thumb. */
  size: number
  /** Object URL for the preview thumbnail. Revoked when the entry is removed. */
  previewUrl: string
}

function ImageInput({
  value,
  onChange,
}: {
  value: UserImage[]
  onChange: (next: UserImage[]) => void
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Drag-reorder state: the index currently being dragged. Reset on
  // drop/dragend. Kept as a ref so the parent doesn't re-render on
  // every dragover tick.
  const dragFromRef = useRef<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  // Revoke object URLs on unmount so we don't leak browser memory for
  // images the user attached then discarded by leaving the page.
  // Captured via ref so the cleanup sees the latest value list (an
  // empty-deps useEffect would close over the initial empty array).
  const valueRef = useRef(value)
  valueRef.current = value
  useEffect(() => {
    return () => {
      valueRef.current.forEach((v) => URL.revokeObjectURL(v.previewUrl))
    }
  }, [])

  async function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setErr(null)
    setBusy(true)
    try {
      const room = MAX_IMAGES - value.length
      if (room <= 0) {
        setErr(`Max ${MAX_IMAGES} images per recipe.`)
        return
      }
      const picked = Array.from(files).slice(0, room)
      const additions: UserImage[] = []
      for (const file of picked) {
        if (file.size > MAX_INPUT_BYTES) {
          setErr(`"${file.name}" is too large (>10MB before compression).`)
          continue
        }
        // HEIC detection: the iOS picker usually transcodes to JPEG, but
        // not always. Check by MIME first, then by filename extension as
        // a fallback for cases where the browser reports image/*.
        const lowerName = file.name.toLowerCase()
        if (
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          lowerName.endsWith(".heic") ||
          lowerName.endsWith(".heif")
        ) {
          setErr(
            `"${file.name}" is HEIC — please re-save as JPEG or PNG. (iPhone: Settings → Camera → Formats → Most Compatible.)`,
          )
          continue
        }
        if (file.type !== "image/jpeg" && file.type !== "image/png") {
          setErr(`"${file.name}": only JPEG or PNG supported.`)
          continue
        }
        try {
          const compressed = await compressImage(file)
          additions.push(compressed)
        } catch (e: any) {
          setErr(`"${file.name}": couldn't process the image.`)
        }
      }
      if (additions.length > 0) onChange([...value, ...additions])
      // Reset the file input so re-picking the same file works.
      if (inputRef.current) inputRef.current.value = ""
    } finally {
      setBusy(false)
    }
  }

  function removeAt(idx: number) {
    const removed = value[idx]
    if (removed) URL.revokeObjectURL(removed.previewUrl)
    onChange(value.filter((_, i) => i !== idx))
  }

  function moveAt(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= value.length || to >= value.length) {
      return
    }
    const next = value.slice()
    const [m] = next.splice(from, 1)
    next.splice(to, 0, m)
    onChange(next)
  }

  // ─── render ──────────────────────────────────────────────────────────
  const atCap = value.length >= MAX_IMAGES

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-stretch gap-3">
        {value.map((img, idx) => {
          const isDragOver = dragOverIdx === idx
          return (
            <div
              key={img.id}
              className={`relative w-24 h-24 sm:w-28 sm:h-28 border ${
                isDragOver ? "border-moss" : "border-border"
              } rounded overflow-hidden bg-surface group transition-colors`}
              draggable
              onDragStart={(e) => {
                dragFromRef.current = idx
                e.dataTransfer.effectAllowed = "move"
                // Required for Firefox.
                e.dataTransfer.setData("text/plain", String(idx))
              }}
              onDragOver={(e) => {
                e.preventDefault()
                e.dataTransfer.dropEffect = "move"
                if (dragOverIdx !== idx) setDragOverIdx(idx)
              }}
              onDragLeave={() => {
                if (dragOverIdx === idx) setDragOverIdx(null)
              }}
              onDrop={(e) => {
                e.preventDefault()
                const from = dragFromRef.current
                if (from != null) moveAt(from, idx)
                dragFromRef.current = null
                setDragOverIdx(null)
              }}
              onDragEnd={() => {
                dragFromRef.current = null
                setDragOverIdx(null)
              }}
            >
              <img
                src={img.previewUrl}
                alt={`Page ${idx + 1}`}
                className="w-full h-full object-cover pointer-events-none"
                draggable={false}
              />
              {/* Order badge — top-left. */}
              <span className="absolute top-1 left-1 font-mono text-[10px] uppercase tracking-wider bg-foreground/80 text-background rounded px-1.5 py-px">
                {idx + 1}
              </span>
              {/* Remove × — top-right. */}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`Remove image ${idx + 1}`}
                className="absolute top-1 right-1 bg-background/85 text-foreground hover:bg-persimmon hover:text-background w-5 h-5 rounded-sm grid place-items-center text-xs leading-none transition-colors"
                title="Remove"
              >
                ×
              </button>
              {/* Position buttons — bottom. Always visible on mobile
                  (no hover state to gate on); fade in on hover/focus
                  on sm+ to keep the editorial-clean look on desktop. */}
              {value.length > 1 && (
                <div className="absolute inset-x-0 bottom-0 flex justify-between bg-background/80 px-1 py-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => moveAt(idx, idx - 1)}
                    disabled={idx === 0}
                    aria-label="Move earlier"
                    className="font-mono text-xs px-1 disabled:opacity-30 hover:text-moss"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => moveAt(idx, idx + 1)}
                    disabled={idx === value.length - 1}
                    aria-label="Move later"
                    className="font-mono text-xs px-1 disabled:opacity-30 hover:text-moss"
                  >
                    →
                  </button>
                </div>
              )}
            </div>
          )
        })}
        {!atCap && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-24 h-24 sm:w-28 sm:h-28 border border-dashed border-border hover:border-moss rounded grid place-items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Add image"
          >
            <div className="text-center">
              <div className="font-display text-3xl leading-none">+</div>
              <div className="font-mono text-[9px] uppercase tracking-wider mt-1">
                {value.length === 0 ? "add photo" : "add another"}
              </div>
            </div>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        // capture hint is mobile-only; desktop ignores it and uses the
        // normal file picker. "environment" suggests the rear camera.
        capture="environment"
        onChange={(e) => addFiles(e.target.files)}
        className="hidden"
        aria-hidden
      />

      {err && (
        <p className="font-sans text-sm text-persimmon">{err}</p>
      )}

      <p className="font-mono text-[11px] text-muted-foreground leading-relaxed">
        JPEG or PNG · up to {MAX_IMAGES} images · compressed in your browser
        before upload. Drag to reorder, or use the ← → arrows that appear on
        hover. Page order matters — Claude reads image 1 first.
      </p>
    </div>
  )
}

/**
 * compressImage — load a File, resize to a max long edge, re-encode to
 * JPEG at JPEG_QUALITY, and hand back a UserImage ready to attach.
 *
 * The original MIME is honored as a CEILING — a PNG stays PNG (no JPEG
 * artifacts on diagrams / line art), a JPEG comes out as JPEG. Most
 * photos coming from a camera roll are already JPEG so this is a no-op
 * for the MIME case.
 */
async function compressImage(file: File): Promise<UserImage> {
  const mime: "image/jpeg" | "image/png" =
    file.type === "image/png" ? "image/png" : "image/jpeg"

  const bitmap = await loadImage(file)
  const { width, height } = scaledDims(bitmap.width, bitmap.height, RESIZE_MAX_EDGE)

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("canvas 2d unsupported")
  ctx.drawImage(bitmap, 0, 0, width, height)

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      mime,
      JPEG_QUALITY,
    )
  })

  const buf = await blob.arrayBuffer()
  const base64 = arrayBufferToBase64(buf)
  const previewUrl = URL.createObjectURL(blob)

  return {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name: file.name,
    base64,
    mime,
    size: blob.size,
    previewUrl,
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("image load failed"))
    }
    img.src = url
  })
}

function scaledDims(w: number, h: number, maxEdge: number) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h }
  if (w >= h) {
    return { width: maxEdge, height: Math.round((h / w) * maxEdge) }
  }
  return { width: Math.round((w / h) * maxEdge), height: maxEdge }
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  // Browser-native btoa works on binary strings; we walk the bytes in
  // chunks so a 2MB image doesn't blow the call stack via apply().
  const bytes = new Uint8Array(buf)
  let binary = ""
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk) as unknown as number[],
    )
  }
  return btoa(binary)
}
